import {Injectable} from "@angular/core";
import {Observable} from "rxjs";

import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {
    IAnswer,
    IQuestion, IQuestionEntry, ISelection, ISentence
} from "../../api/data-model";
import {HttpClient} from "@angular/common/http";
import {ConversionPipelineService} from "./conversion.pipeline.service";
import {UtilitiesService} from "../utilities.service";
import {AudioState, Scilearn} from "../../constants";


export class QuestionPipelineService {
    public static skillValues: string[] = [
        "Ask Questions",
        "Author's View",
        "Cause and Effect",
        "Check for Understanding",
        "Compare and Contrast",
        "Context Clues",
        "Fact and Opinion; Evaluate Evidence",
        "Figurative Language",
        "Guiding Thinking",
        "Inference/Draw Conclusions",
        "Main Idea and Details",
        "Make Connections",
        "Nonfiction Text Features",
        "Predict",
        "Retell/Summarize",
        "Schema/Activating Prior Knowledge",
        "Sequence",
        "Story Elements",
        "Visualize"];

    public static thinkingValues: string[] = ["Inferential", "Literal", "Evaluation", "Analysis"];

    private static presentation = {Quiz: Scilearn.QUIZ_QUESTION, "Think About It": Scilearn.THINK_ABOUT_IT};
    private static type = {
        "Multiple Choice": Scilearn.QUESTIONTYPE_MULTICHOICE,
        "Multiple Correct": Scilearn.QUESTIONTYPE_MULTICHECKS,
        Prompt: Scilearn.QUESTIONTYPE_PROMPT,
        "True/False": Scilearn.QUESTIONTYPE_TRUEFALSE,
        "Open Book": Scilearn.QUESTIONTYPE_OPENBOOK
    };

    private headers: Array<string>;
    private parsedQuestions: Array<Array<string>>;

    constructor(public http: HttpClient, private logService: BugsnagLoggingService) {
    }

    public setupImportData(response: any): Set<string> {
        let questionLines = response.split("\n");
        this.headers = questionLines[0].split("\t");
        this.headers.forEach((header, index) => {
            this.headers[index] = header.trim();
        });
        console.log(this.headers);
        questionLines.shift();

        this.parsedQuestions = [];
        questionLines.forEach((line) => {
            this.parsedQuestions.push(line.trim().split("\t"));
        });
        const titleIndex = this.headers.findIndex((header) => header.startsWith("Title"));
        let selections = new Set(this.parsedQuestions.map((questionLine) =>
            questionLine[titleIndex].replace(ConversionPipelineService.PUNCTUATION, "")
                .replace(ConversionPipelineService.SPACE, "")).filter(title => !!title));
        console.log(selections);
        return selections;
    }

    public addQuestions(selection: ISelection): void {
        this.removeTAIAnchors(selection);
        const titleIndex = this.headers.findIndex((header) => header.startsWith("Title"));
        const questionsForSelection = this.parsedQuestions
            .filter(questionLine => questionLine[titleIndex]
                .replace(ConversionPipelineService.PUNCTUATION, "")
                .replace(ConversionPipelineService.SPACE, "") === selection.metaData.referenceStory);
        selection.questionEntries = this.convertQuestionsForImport(selection, questionsForSelection);
    }

    private convertQuestionsForImport(selection: ISelection, parsedArray: Array<Array <string>>): IQuestionEntry[] {
        if (!this.headers || parsedArray.length === 0) {
            console.log("Empty import question array!");
            return;
        }
        let questions: Array<IQuestionEntry> = [];
        let errorDetail = "";
        parsedArray.forEach((parsedEntry) => {
            parsedEntry = parsedEntry.map((s) => s.trim());
            try {
                let question: IQuestionEntry = this.getQuestionFromImport(parsedEntry, selection);

                questions.push(question);
            }
            catch (e) {
                errorDetail += "error on line: " + parsedEntry.toString() +
                    " for selection " + selection.metaData.referenceStory + ":" + e.message + "\n";
            }
        });
        if (errorDetail) {
            window.alert("Some questions not imported due to errors or inconsistencies.  Detail:\n" + errorDetail);
        }
        return questions;
    }

    public getQuestionFromImport(entryArray: Array<string>, selection: ISelection): IQuestionEntry {
        let questionEntry: IQuestionEntry = {
            answers: [],
            context: "",
            id: "",
            presentation: "",
            question: {text: "", audio: ""},
            skill: "",
            thinking: "",
            type: undefined
        };
        let lightbulbSentence = "";
        for (let [index, header] of this.headers.entries())  {
            const entryValue = entryArray[index];
            // work around extra instruction in this header
            if (header.startsWith("Think About It Lightbulb Location")) {
                header = "Think About It Lightbulb Location";
            }
            switch (header) {
                case "Question Presentation":
                    if (!QuestionPipelineService.presentation[entryValue]) {
                        throw new Error(entryValue ? "Invalid question presentation: " + entryValue
                            : "Missing question presentation");
                    }
                    questionEntry.presentation = QuestionPipelineService.presentation[entryValue];
                    break;
                case "Think About It Lightbulb Location":
                    lightbulbSentence = entryValue.replace(/\s+/g, " ").trim();
                    break;
                case "Question Type":
                    if (!QuestionPipelineService.type[entryValue]) {
                        throw new Error(entryValue ? "Invalid question type: " + entryValue : "Missing question type");
                    }
                    questionEntry.type = QuestionPipelineService.type[entryValue];
                    break;
                case "Level of Thinking":
                    if (!QuestionPipelineService.thinkingValues.includes(entryValue)) {
                        throw new Error(entryValue ? "Invalid Level of Thinking: " + entryValue
                            : "Missing Level of Thinking");
                    }
                    questionEntry.thinking = entryValue;

                    break;
                case "Comprehension Skill/Strategy":
                    if (!QuestionPipelineService.skillValues.includes(entryValue)) {
                        throw new Error(entryValue ? "Invalid Strategy/Skill: " + entryValue : "Missing Skill/Strategy");
                    }
                    questionEntry.skill = entryValue;
                    break;
                case "Question Text":
                    if (!entryValue.trim()) {
                        throw new Error("Missing question text");
                    }
                    questionEntry.question.text = entryValue;
                    break;
                case "Answer 1":
                case "Answer 2":
                case "Answer 3":
                case "Answer 4":
                    if (!entryValue || !entryValue.trim()) {
                        break;
                    }
                    let answer: IAnswer = {audio: "", text: ""};
                    if (entryValue.endsWith("*") || entryValue.endsWith("+")) {
                        answer.correct = true;
                    }
                    answer.text = entryValue.replace(/\s+/g, " ").trim().replace(/\s*[*+]$/, "");
                    questionEntry.answers.push(answer);
                    break;
                default:
                    console.log("Data not processed: " + header + ": " + entryValue);
                    break;
            }

        }
        questionEntry.id = UtilitiesService.newUuid(); // this is just a placeholder
        if (questionEntry.presentation === Scilearn.THINK_ABOUT_IT) {
            let sentence = this.getSentence(lightbulbSentence, selection);
            if (sentence?.words?.length) {
                sentence.words[0].startQuestionId = questionEntry.id;
                sentence.words[sentence.words.length - 1].questionId = questionEntry.id;
            } else {
                throw new Error("Can't link Think About It lightbulb to text");
            }
        }
        if (questionEntry.type === "OpenBook") {
            let openBookAnswer = questionEntry.answers[0].text;
            let sentence = this.getSentence(openBookAnswer, selection);
            questionEntry.answers[0].text = "";
            if (!!sentence) {
                questionEntry.answers[0].answerSentenceId = sentence.id;
            } else {
                throw new Error("Can't link Open Book answer to text: " + openBookAnswer);
            }
            questionEntry.answers[0].correct = true;

        }
        // NOTE: we are relying on in-sheet validation in Google sheets, and not doing complex validation here for
        // e.g. inconsistencies between type and number of answers
        return this.setAudioState(questionEntry, AudioState.UNRECORDED);
    }

    public setAudioState(questionEntry, audioState): IQuestionEntry {
        // NOTE: not doing anything with hint audio, RACPT does not support adding hints
        questionEntry.question.audioState = audioState;
        if (questionEntry.question.answers && questionEntry.question.answers.length > 0) {
            questionEntry.question.answers = questionEntry.question.answers.map(
                answer => questionEntry.type === Scilearn.QUESTIONTYPE_OPENBOOK ? answer : {...answer, audioState});
        }
        return questionEntry;
    }

    private getSentence(sentenceString: string, selection: ISelection): ISentence {
        for (let text of selection.text) {
            if (text.sentences) {
                for (const sentence of text.sentences) {
                    const wordArray = sentence.words.map(word => {
                        const prefix = word.prefix ? word.prefix : "";
                        const punct = word.separator ? word.separator : "";
                        return prefix + word.word + punct;
                    });
                    const sentenceToCompare = wordArray.join("").trim();
                    if (UtilitiesService.convertPunctuationToASCII(sentenceToCompare)
                        === UtilitiesService.convertPunctuationToASCII(sentenceString).trim().replace(/[*+]$/, "")) {
                        return sentence;
                    }
                }
            }
        }
        console.log("could not find match for: " + sentenceString);
        return null;
    }

    private removeTAIAnchors(selection: ISelection): ISentence {
        for (let text of selection.text) {
            if (text.sentences) {
                for (const sentence of text.sentences) {
                    sentence.words = sentence.words.map(word => {
                        delete word.startQuestionId;
                        delete word.questionId;
                        return word;
                    });
                }
            }
        }
        return null;
    }


}
